import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  Button,
  Form,
  Modal,
  Alert,
  Badge,
  Spinner,
  InputGroup,
  Pagination,
  ProgressBar,
  Offcanvas,
  ListGroup,
  Image
} from 'react-bootstrap';
import ProductImage from '../products/ProductImage';
import { 
  Package, 
  RefreshCw, 
  Plus,
  AlertTriangle,
  Search,
  Filter,
  Edit,
  Eye,
  Menu,
  Grid3x3,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Calculator,
  Info
} from 'lucide-react';

import stockService from '../../services/stockService'; 
import productService from '../../services/productService';
import { useAuth } from '../../contexts/AuthContext';

const StockManagement = () => {
  // États principaux
  const [stockItems, setStockItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // États des modals
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [showMovementHistoryModal, setShowMovementHistoryModal] = useState(false);
  const [selectedStockItem, setSelectedStockItem] = useState(null);
  const [selectedProductMovements, setSelectedProductMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  
  // États responsive
  const [viewMode, setViewMode] = useState('cards');
  const [showFiltersOffcanvas, setShowFiltersOffcanvas] = useState(false);
  const [showStatsOffcanvas, setShowStatsOffcanvas] = useState(false);
  
  // États des filtres et pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockFilter, setStockFilter] = useState('');
  const [sortBy, setSortBy] = useState('productName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // États du formulaire mouvement
  const [movementForm, setMovementForm] = useState({
    productId: '',
    type: 'ENTREE',
    quantity: '',
    countedQuantity: '',
    unitCost: '',
    reason: '',
    notes: ''
  });
  
  // États du formulaire seuil
  const [thresholdForm, setThresholdForm] = useState({
    productId: '',
    threshold: ''
  });
  
  // États de soumission et erreurs
  const [submitting, setSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const { user } = useAuth();
  const isManager = user?.role === 'MANAGER';

  // Fonctions utilitaires pour formatage des quantités avec signes
  const formatMovementQuantity = (movement) => {
    const quantity = movement.quantity || 0;
    const type = movement.type?.toUpperCase();
    
    const isOutgoing = type === 'SORTIE' || type === 'OUT' || 
                       (type === 'AJUSTEMENT' && quantity < 0);
    
    if (isOutgoing && quantity > 0) {
      return `-${quantity}`;
    } else if (type === 'ENTREE' || type === 'IN') {
      return `+${Math.abs(quantity)}`;
    } else if (type === 'AJUSTEMENT') {
      return quantity > 0 ? `+${quantity}` : `${quantity}`;
    }
    
    return quantity;
  };

  const getMovementQuantityColor = (movement) => {
    const type = movement.type?.toUpperCase();
    const quantity = movement.quantity || 0;
    
    if (type === 'ENTREE' || type === 'IN' || (type === 'AJUSTEMENT' && quantity > 0)) {
      return 'text-success';
    } else if (type === 'SORTIE' || type === 'OUT' || (type === 'AJUSTEMENT' && quantity < 0)) {
      return 'text-danger';
    } else {
      return 'text-info';
    }
  };

  // CORRECTION: Calculer la différence pour les ajustements
  const calculateAdjustmentDifference = () => {
    const currentStock = selectedStockItem?.quantity || selectedStockItem?.currentStock || 0;
    const countedQuantity = parseFloat(movementForm.countedQuantity) || 0;
    const difference = countedQuantity - currentStock;
    
    return {
      currentStock,
      countedQuantity,
      difference,
      isIncrease: difference > 0,
      isDecrease: difference < 0
    };
  };

  // États pour les catégories uniques
  const [uniqueCategories, setUniqueCategories] = useState([]);

  useEffect(() => {
    loadStockData();
    loadProducts();
  }, []);

  // Extraire les catégories uniques du stock
  useEffect(() => {
    if (stockItems.length > 0) {
      const categories = [...new Set(stockItems.map(item => item.categoryName).filter(Boolean))];
      setUniqueCategories(categories);
    }
  }, [stockItems]);

  // Fonction de debug pour les images
  const debugProductImages = (products) => {
    
    
    products.slice(0, 3).forEach((product, index) => {
      
    });
    
  };

  const loadStockData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      
      const response = await stockService.getStock();
      
      if (response.success) {
        const stockData = Array.isArray(response.data) ? response.data : [];
        
        // Debug des images
        debugProductImages(stockData);
        
        // Formatter les données
        const formattedStock = stockData.map(item => ({
          id: item.id,
          productId: item.productId || item.product_id,
          productName: item.productName || item.product_name || item.product?.name || 'Produit sans nom',
          productSku: item.productSku || item.product_sku || item.product?.sku || '',
          quantity: item.quantity || item.currentStock || 0,
          minThreshold: item.minThreshold || item.min_threshold || 0,
          maxThreshold: item.maxThreshold || item.max_threshold || null,
          categoryName: item.categoryName || item.category_name || item.product?.category?.name || 'Non catégorisé',
          unitCost: item.unitCost || item.unit_cost || 0,
          lastMovement: item.lastMovement || item.last_movement,
          lastUpdate: item.lastUpdate || item.last_updated || item.updatedAt,
          
          // CORRECTION: Gestion des images - essayer toutes les variations
          image: item.image || item.imageUrl || item.image_url || item.product?.image || item.product?.imageUrl,
          imageUrl: item.imageUrl || item.image_url || item.image || item.product?.imageUrl || item.product?.image,
          
          // Calculs dérivés
          isOutOfStock: (item.quantity || item.currentStock || 0) === 0,
          isLowStock: (item.quantity || item.currentStock || 0) > 0 && 
                     (item.quantity || item.currentStock || 0) <= (item.minThreshold || item.min_threshold || 0),
          stockValue: (item.quantity || item.currentStock || 0) * (item.unitCost || item.unit_cost || 0),
          
          // Couleurs et statuts
          stockLevelColor: (() => {
            const qty = item.quantity || item.currentStock || 0;
            const threshold = item.minThreshold || item.min_threshold || 0;
            if (qty === 0) return 'danger';
            if (qty <= threshold) return 'warning';
            return 'success';
          })()
        }));
        
        // Debug après formatage
        debugProductImages(formattedStock);
        
        setStockItems(formattedStock);
      } else {
        setError(response.message || 'Erreur lors du chargement du stock');
        setStockItems([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement du stock:', error);
      setError('Erreur lors du chargement du stock');
      setStockItems([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      
      const response = await productService.getProducts({ size: 1000 });
      
      if (response.success) {
        const productsData = response.data.content || response.data || [];
        setProducts(productsData);
      } else {
        console.warn('⚠️ Erreur chargement produits:', response.message);
        setProducts([]);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des produits:', error);
      setProducts([]);
    }
  };

  // Charger l'historique des mouvements pour un produit
  const handleViewMovements = async (stockItem) => {
    setSelectedStockItem(stockItem);
    setShowMovementHistoryModal(true);
    setLoadingMovements(true);
    
    try {
      
      const response = await stockService.getStockMovements({ 
        productId: stockItem.productId,
        limit: 50 
      });
      
      if (response.success) {
        const movementsData = Array.isArray(response.data) ? 
          response.data : 
          (response.data?.content || response.data?.data || []);
        
        // Filtrage côté client si le backend ne filtre pas par productId
        let filteredMovements = movementsData;
        
        if (movementsData.length > 0 && stockItem.productId) {
          filteredMovements = movementsData.filter(movement => {
            const movementProductId = movement.productId || movement.product_id || movement.product?.id;
            return String(movementProductId) === String(stockItem.productId);
          });
          
        }
        
        // Formater les mouvements
        const formattedMovements = filteredMovements.map(movement => ({
          id: movement.id,
          type: movement.type || movement.movementType || movement.movement_type,
          quantity: movement.quantity,
          date: movement.date || movement.createdAt || movement.created_at,
          reason: movement.reason || movement.notes || movement.description,
          reference: movement.reference || movement.ref,
          user: movement.user || movement.userName || movement.user_name || movement.userFullName,
          unitCost: movement.unitCost || movement.unit_cost || 0
        }));
        
        // Trier par date (plus récent en premier)
        formattedMovements.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA;
        });
        
        setSelectedProductMovements(formattedMovements);
      } else {
        setSelectedProductMovements([]);
        console.warn('⚠️ Aucun mouvement trouvé:', response.message);
        if (response.message !== 'Fonctionnalité de suivi des mouvements en cours de développement') {
          setError(response.message || 'Erreur lors du chargement des mouvements');
        }
      }
    } catch (error) {
      console.error('❌ Erreur chargement mouvements:', error);
      setSelectedProductMovements([]);
      setError('Erreur de connexion');
    } finally {
      setLoadingMovements(false);
    }
  };

  // Fonctions de filtrage
  const getFilteredStock = () => {
    if (!Array.isArray(stockItems)) return [];
    
    let filtered = stockItems.filter(item => {
      const searchMatch = !searchTerm || 
        (item.productName && item.productName.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.productSku && item.productSku.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.categoryName && item.categoryName.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const categoryMatch = !categoryFilter || item.categoryName === categoryFilter;
      
      const stockMatch = !stockFilter || (() => {
        switch (stockFilter) {
          case 'low': return item.isLowStock && !item.isOutOfStock;
          case 'empty': return item.isOutOfStock;
          case 'good': return !item.isLowStock && !item.isOutOfStock;
          default: return true;
        }
      })();
      
      return searchMatch && categoryMatch && stockMatch;
    });

    // Tri
    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : 1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  };

  // Pagination
  const getPaginatedStock = () => {
    const filtered = getFilteredStock();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredStock().length / itemsPerPage);
  };

  // Gestion des modals
  const handleCreateMovement = (stockItem = null) => {
    setSelectedStockItem(stockItem);
    setMovementForm({
      productId: stockItem?.productId || '',
      type: 'ENTREE',
      quantity: '',
      countedQuantity: '',
      unitCost: '',
      reason: '',
      notes: ''
    });
    setFormErrors({});
    setShowMovementModal(true);
  };

  /* ✅ Fonction d'ouverture du modal corrigée */
const handleSetThreshold = (item) => {
  setSelectedStockItem(item);
  setThresholdForm({
    threshold: item.minThreshold?.toString() || '5',
    maxThreshold: item.maxThreshold?.toString() || ''
  });
  setFormErrors({});
  setShowThresholdModal(true);
};

  // Gestion des formulaires
  const handleMovementFormChange = (e) => {
    const { name, value } = e.target;
    setMovementForm(prev => ({ ...prev, [name]: value }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validation spécifique aux ajustements
  const validateAdjustmentForm = () => {
    const errors = {};
    
    if (!movementForm.productId) {
      errors.productId = 'Veuillez sélectionner un produit';
    }
    
    if (!movementForm.countedQuantity || parseFloat(movementForm.countedQuantity) < 0) {
      errors.countedQuantity = 'Veuillez saisir une quantité comptée valide';
    }
    
    if (!movementForm.reason?.trim()) {
      errors.reason = 'Veuillez indiquer la raison de l\'ajustement';
    }
    
    return errors;
  };

  // CORRECTION PRINCIPALE: Soumission du mouvement avec logique d'inventaire corrigée
  const handleSubmitMovement = async (e) => {
    e.preventDefault();
    
    let validationErrors = {};
    
    if (movementForm.type === 'AJUSTEMENT') {
      validationErrors = validateAdjustmentForm();
    } else {
      if (!movementForm.productId) {
        validationErrors.productId = 'Veuillez sélectionner un produit';
      }
      
      if (!movementForm.quantity || parseFloat(movementForm.quantity) <= 0) {
        validationErrors.quantity = 'Veuillez saisir une quantité valide';
      }
    }
    
    if (Object.keys(validationErrors).length > 0) {
      setFormErrors(validationErrors);
      return;
    }
    
    try {
      setSubmitting(true);
      setFormErrors({});
      setError(null);
      
      let movementData;
      
      if (movementForm.type === 'AJUSTEMENT') {
        // CORRECTION: Pour les inventaires, envoyer la quantité comptée directement
        const currentStock = selectedStockItem?.quantity || selectedStockItem?.currentStock || 0;
        const countedQuantity = parseFloat(movementForm.countedQuantity);
        const difference = countedQuantity - currentStock;
        
        
        
        movementData = {
          productId: movementForm.productId,
          type: 'AJUSTEMENT',
          quantity: countedQuantity, // CORRECTION: Quantité comptée = nouveau stock
          reason: movementForm.reason,
          notes: `Inventaire: Stock ajusté de ${currentStock} à ${countedQuantity} unités (${difference > 0 ? '+' : ''}${difference}). ${movementForm.notes || ''}`.trim(),
          reference: `INV-${Date.now()}`,
          // Informations supplémentaires pour traçabilité
          oldQuantity: currentStock,
          adjustmentType: 'INVENTORY'
        };
      } else {
        // Logique classique pour ENTREE/SORTIE
        movementData = {
          productId: movementForm.productId,
          type: movementForm.type,
          quantity: parseFloat(movementForm.quantity),
          unitCost: movementForm.type === 'ENTREE' ? 
                   (parseFloat(movementForm.unitCost) || 0) : undefined,
          reason: movementForm.reason,
          notes: movementForm.notes
        };
      }
      
      
      const response = await stockService.createStockMovement(movementData);
      
      if (response.success) {
        setSuccess('Mouvement créé avec succès !');
        setShowMovementModal(false);
        
        // Recharger les données
        await loadStockData();
        
        // Auto-masquer le message de succès
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setFormErrors({ submit: response.message || 'Erreur lors de la création' });
      }
    } catch (error) {
      console.error('❌ Erreur création mouvement:', error);
      setFormErrors({ submit: 'Erreur de connexion' });
    } finally {
      setSubmitting(false);
    }
  };

  // Soumission du seuil
  const handleSubmitThreshold = async (e) => {
  e.preventDefault();
  
  if (!selectedStockItem) return;
  
  setSubmitting(true);
  setFormErrors({});
  
  try {
    // ✅ VALIDATION côté client
    const minThreshold = parseInt(thresholdForm.threshold);
    const maxThreshold = thresholdForm.maxThreshold ? parseInt(thresholdForm.maxThreshold) : null;
    
    if (isNaN(minThreshold) || minThreshold < 0) {
      setFormErrors({ threshold: 'Le seuil minimum doit être un nombre positif' });
      return;
    }
    
    if (maxThreshold !== null && maxThreshold <= minThreshold) {
      setFormErrors({ 
        maxThreshold: 'Le seuil maximum doit être supérieur au seuil minimum' 
      });
      return;
    }
    
    // ✅ CORRECTION PRINCIPALE : Inclure newQuantity avec la quantité actuelle
    const adjustmentData = {
      newQuantity: selectedStockItem.quantity, // ← AJOUT CRUCIAL : quantité actuelle inchangée
      minThreshold: minThreshold,
      maxThreshold: maxThreshold,
      reason: 'Mise à jour des seuils d\'alerte'
    };
    
    
    // Appel API avec la bonne structure
    const response = await stockService.updateStockThreshold(selectedStockItem.productId, adjustmentData);
    
    if (response.success) {
      setSuccess('Seuils mis à jour avec succès');
      setShowThresholdModal(false);
      setSelectedStockItem(null);
      setThresholdForm({ threshold: '', maxThreshold: '' });
      
      // Recharger les données
      await loadStockData();
    } else {
      setFormErrors({ submit: response.message || 'Erreur lors de la mise à jour' });
    }
  } catch (error) {
    console.error('❌ Erreur mise à jour seuils:', error);
    setFormErrors({ 
      submit: error.response?.data?.message || 'Erreur de connexion' 
    });
  } finally {
    setSubmitting(false);
  }
};

  // Fonctions utilitaires existantes
  const getMovementIcon = (type) => {
    switch (type?.toUpperCase()) {
      case 'ENTREE':
      case 'IN':
        return '📥';
      case 'SORTIE':
      case 'OUT':
        return '📤';
      case 'AJUSTEMENT':
      case 'ADJUSTMENT':
        return '⚖️';
      default:
        return '📦';
    }
  };

  const getMovementLabel = (type) => {
    switch (type?.toUpperCase()) {
      case 'ENTREE':
      case 'IN':
        return 'Entrée';
      case 'SORTIE':
      case 'OUT':
        return 'Sortie';
      case 'AJUSTEMENT':
      case 'ADJUSTMENT':
        return 'Ajustement';
      default:
        return 'Mouvement';
    }
  };

  const getMovementColor = (type) => {
    switch (type?.toUpperCase()) {
      case 'ENTREE':
      case 'IN':
        return 'success';
      case 'SORTIE':
      case 'OUT':
        return 'danger';
      case 'AJUSTEMENT':
      case 'ADJUSTMENT':
        return 'info';
      default:
        return 'secondary';
    }
  };

  // Calculs pour les statistiques
  const getStockStats = () => {
    const total = stockItems.length;
    const outOfStock = stockItems.filter(item => item.isOutOfStock).length;
    const lowStock = stockItems.filter(item => item.isLowStock && !item.isOutOfStock).length;
    const goodStock = total - outOfStock - lowStock;
    const totalValue = stockItems.reduce((sum, item) => sum + item.stockValue, 0);

    return { total, outOfStock, lowStock, goodStock, totalValue };
  };

  const stats = getStockStats();

  // CORRECTION: Composant StockCard avec meilleure gestion des images
  const StockCard = ({ item }) => (
    <Card className="h-100 stock-card">
      <Card.Body className="d-flex flex-column">
        <div className="d-flex align-items-start gap-3 mb-3">
          <div className="product-image-container">
            
            
            {/* Utilisation d'une image simple en fallback */}
            {(item.imageUrl || item.image) ? (
              <img
                src={item.imageUrl || item.image}
                alt={item.productName}
                style={{
                  width: '50px',
                  height: '50px',
                  objectFit: 'cover',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  backgroundColor: '#f8f9fa'
                }}
                onError={(e) => {
                  console.error(`❌ Erreur image ${item.productName}:`, e.target.src);
                  e.target.style.display = 'none';
                }}
                onLoad={() => console.log(`✅ Image OK pour ${item.productName}`)}
              />
            ) : (
              <div 
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '8px',
                  border: '1px solid #dee2e6',
                  backgroundColor: '#e9ecef',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#6c757d'
                }}
              >
                <Package size={20} />
              </div>
            )}
          </div>
          
          <div className="flex-grow-1 min-w-0">
            <h6 className="mb-1 text-truncate" title={item.productName}>
              {item.productName}
            </h6>
            <small className="text-muted d-block mb-2">
              {item.categoryName}
            </small>
            {/* Debug URL en mode développement */}
            {process.env.NODE_ENV === 'development' && (
              <small className="text-info d-block" style={{ fontSize: '10px', wordBreak: 'break-all' }}>
                {/* IMG: {(item.imageUrl || item.image)?.substring(0, 50) || 'Aucune'}... */}
              </small>
            )}
          </div>
        </div>
        
        <div className="d-flex justify-content-center mb-3">
          <Badge 
            bg={item.isOutOfStock ? 'danger' : 
                item.isLowStock ? 'warning' : 'success'}
            className="px-3 py-2"
          >
            {item.isOutOfStock ? 'Rupture' : 
             item.isLowStock ? 'Stock faible' : 'OK'}
          </Badge>
        </div>
        
        <Row className="text-center mb-3">
          <Col xs={6}>
            <div className="stock-metric">
              <div className={`h5 mb-0 text-${item.stockLevelColor}`}>
                {item.quantity}
              </div>
              <small className="text-muted">Stock</small>
            </div>
          </Col>
          <Col xs={6}>
            <div className="stock-metric">
              <div className="h6 mb-0">{item.minThreshold}</div>
              <small className="text-muted">Seuil</small>
            </div>
          </Col>
        </Row>
        
        <div className="d-flex gap-2 mb-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleCreateMovement(item)}
            className="flex-fill"
          >
            <Plus size={14} className="me-1" />
            Mouvement
          </Button>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleSetThreshold(item)}
          >
            <Edit size={14} />
          </Button>
        </div>
        
        <div className="d-grid">
          <Button
            variant="outline-info"
            size="sm"
            onClick={() => handleViewMovements(item)}
          >
            <Eye size={14} className="me-1" />
            Voir mouvements
          </Button>
        </div>
        
        <div className="mt-2">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <small className="text-muted">Niveau de stock</small>
            <small className="text-muted">
              {Math.round((item.quantity / Math.max(item.maxThreshold, item.quantity)) * 100)}%
            </small>
          </div>
          <ProgressBar 
            now={Math.min((item.quantity / Math.max(item.maxThreshold, item.quantity)) * 100, 100)}
            variant={item.stockLevelColor}
            style={{ height: '4px' }}
          />
        </div>
      </Card.Body>
    </Card>
  );

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3">Chargement du stock...</p>
        </div>
      </Container>
    );
  }

  return (
    <div className="stock-container">
      <Container fluid className="stock-content">
        {/* Messages de succès et erreur */}
        {success && (
          <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="mb-3">
            {success}
          </Alert>
        )}
        
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
            {error}
          </Alert>
        )}

        {/* En-tête */}
        <Card className="stock-header">
          <div className="header-content">
            <Package size={40} className="header-icon" />
            <div className="flex-grow-1">
              <h1 className="stock-title">Gestion du Stock</h1>
              <p className="stock-subtitle">
                {stats.total} produits • {stats.outOfStock} en rupture • {stats.lowStock} stock faible
              </p>
            </div>
            <div className="header-actions d-none d-lg-flex">
              <Button variant="outline-secondary" onClick={loadStockData} className="me-2">
                <RefreshCw size={16} />
              </Button>
              <Button variant="primary" onClick={() => handleCreateMovement()}>
                <Plus size={16} className="me-2" />
                Nouveau mouvement
              </Button>
            </div>
          </div>
        </Card>

        {/* Statistiques rapides */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="stat-card border-primary" onClick={() => setStockFilter('')} style={{ cursor: 'pointer' }}>
              <Card.Body className="text-center">
                <div className="stat-icon bg-primary">
                  <Package className="text-white" size={24} />
                </div>
                <h4 className="stat-number">{stats.total}</h4>
                <p className="stat-label">Total produits</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card border-success" onClick={() => setStockFilter('good')} style={{ cursor: 'pointer' }}>
              <Card.Body className="text-center">
                <div className="stat-icon bg-success">
                  <TrendingUp className="text-white" size={24} />
                </div>
                <h4 className="stat-number">{stats.goodStock}</h4>
                <p className="stat-label">Stock OK</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card border-warning" onClick={() => setStockFilter('low')} style={{ cursor: 'pointer' }}>
              <Card.Body className="text-center">
                <div className="stat-icon bg-warning">
                  <AlertTriangle className="text-white" size={24} />
                </div>
                <h4 className="stat-number">{stats.lowStock}</h4>
                <p className="stat-label">Stock faible</p>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="stat-card border-danger" onClick={() => setStockFilter('empty')} style={{ cursor: 'pointer' }}>
              <Card.Body className="text-center">
                <div className="stat-icon bg-danger">
                  <TrendingDown className="text-white" size={24} />
                </div>
                <h4 className="stat-number">{stats.outOfStock}</h4>
                <p className="stat-label">Rupture</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtres */}
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <Search size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Rechercher un produit..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">Toutes les catégories</option>
                  {uniqueCategories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                >
                  <option value="">Tous les niveaux</option>
                  <option value="good">Stock OK</option>
                  <option value="low">Stock faible</option>
                  <option value="empty">Rupture</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <div className="d-flex gap-2">
                  <Button
                    variant={viewMode === 'cards' ? 'primary' : 'outline-secondary'}
                    onClick={() => setViewMode('cards')}
                    size="sm"
                  >
                    <Grid3x3 size={16} />
                  </Button>
                  <Button
                    variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                    onClick={() => setViewMode('table')}
                    size="sm"
                  >
                    <Menu size={16} />
                  </Button>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Contenu principal */}
        {viewMode === 'cards' ? (
          <Row>
            {getPaginatedStock().map(item => (
              <Col key={item.id} lg={3} md={4} sm={6} className="mb-4">
                <StockCard item={item} />
              </Col>
            ))}
          </Row>
        ) : (
          <Card>
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Catégorie</th>
                    <th className="text-center">Stock</th>
                    <th className="text-center">Seuil</th>
                    <th className="text-center">État</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedStock().map(item => (
                    <tr key={item.id}>
                      <td>
                        <div className="d-flex align-items-center gap-3">
                          <div className="product-image-table">
                            {console.log(`🖼️ Image tableau pour "${item.productName}":`, item.imageUrl || item.image)}
                            {(item.imageUrl || item.image) ? (
                              <img
                                src={item.imageUrl || item.image}
                                alt={item.productName}
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  objectFit: 'cover',
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6'
                                }}
                                onError={(e) => {
                                  console.error(`❌ Erreur image tableau ${item.productName}`);
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div 
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '6px',
                                  border: '1px solid #dee2e6',
                                  backgroundColor: '#e9ecef',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Package size={16} />
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="fw-semibold">{item.productName}</div>
                            <small className="text-muted">{item.productSku}</small>
                          </div>
                        </div>
                      </td>
                      <td>
                        <small>{item.categoryName}</small>
                      </td>
                      <td className="text-center">
                        <span className={`fw-bold text-${item.stockLevelColor}`}>
                          {item.quantity}
                        </span>
                      </td>
                      <td className="text-center">
                        <small>{item.minThreshold}</small>
                      </td>
                      <td className="text-center">
                        <Badge bg={item.stockLevelColor}>
                          {item.isOutOfStock ? 'Rupture' : 
                           item.isLowStock ? 'Faible' : 'OK'}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleCreateMovement(item)}
                            title="Créer mouvement"
                          >
                            <Plus size={14} />
                          </Button>
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => handleViewMovements(item)}
                            title="Voir mouvements"
                          >
                            <Eye size={14} />
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleSetThreshold(item)}
                            title="Modifier seuil"
                          >
                            <Edit size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card>
        )}

        {/* Pagination */}
        {getTotalPages() > 1 && (
          <div className="d-flex justify-content-center mt-4">
            <Pagination>
              <Pagination.First 
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev 
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              />
              
              {[...Array(getTotalPages())].slice(
                Math.max(0, currentPage - 3),
                Math.min(getTotalPages(), currentPage + 2)
              ).map((_, index) => {
                const page = Math.max(0, currentPage - 3) + index + 1;
                return (
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                );
              })}
              
              <Pagination.Next 
                onClick={() => setCurrentPage(Math.min(getTotalPages(), currentPage + 1))}
                disabled={currentPage === getTotalPages()}
              />
              <Pagination.Last 
                onClick={() => setCurrentPage(getTotalPages())}
                disabled={currentPage === getTotalPages()}
              />
            </Pagination>
          </div>
        )}
      </Container>

      {/* MODAL CRÉER MOUVEMENT AVEC CORRECTION AJUSTEMENT */}
      <Modal show={showMovementModal} onHide={() => setShowMovementModal(false)} size="lg">
        <Form onSubmit={handleSubmitMovement}>
          <Modal.Header closeButton>
            <Modal.Title>
              <Plus size={20} className="me-2" />
              Créer un mouvement de stock
              {selectedStockItem && (
                <Badge bg="primary" className="ms-2">
                  {selectedStockItem.productName}
                </Badge>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {formErrors.submit && (
              <Alert variant="danger">{formErrors.submit}</Alert>
            )}
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Produit *</Form.Label>
                  <Form.Select
                    name="productId"
                    value={movementForm.productId}
                    onChange={handleMovementFormChange}
                    isInvalid={!!formErrors.productId}
                  >
                    <option value="">Sélectionner un produit...</option>
                    {products.map(product => (
                      <option key={product.id} value={product.id}>
                        {product.name} {product.sku && `(${product.sku})`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.productId}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type de mouvement *</Form.Label>
                  <Form.Select
                    name="type"
                    value={movementForm.type}
                    onChange={handleMovementFormChange}
                  >
                    <option value="ENTREE">📥 Entrée (Réception)</option>
                    <option value="SORTIE">📤 Sortie (Expédition)</option>
                    <option value="AJUSTEMENT">⚖️ Ajustement (Inventaire)</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {/* CORRECTION: Affichage conditionnel selon le type */}
            {movementForm.type === 'AJUSTEMENT' ? (
              // Section ajustement avec explication améliorée
              <>
                {selectedStockItem && (
                  <Alert variant="info" className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Info size={20} />
                      <strong>Inventaire : Stock actuel = {selectedStockItem.quantity} unité(s)</strong>
                    </div>
                    <div className="mb-2">
                      <small className="fw-semibold">Comment ça marche :</small>
                      <ul className="mb-0 mt-1" style={{ fontSize: '0.9em' }}>
                        <li>Comptez physiquement les articles en stock</li>
                        <li>Saisissez la quantité réellement trouvée</li>
                        <li>Le système mettra à jour le stock avec cette quantité</li>
                        <li>La différence sera enregistrée dans l'historique</li>
                      </ul>
                    </div>
                    <small className="text-muted">
                      <strong>Exemple :</strong> Si vous comptez 60 unités, le stock passera à 60 unités 
                      (différence de {selectedStockItem.quantity < 60 ? `+${60 - selectedStockItem.quantity}` : `${60 - selectedStockItem.quantity}`} sera notée).
                    </small>
                  </Alert>
                )}

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Quantité comptée *</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>
                          <Calculator size={16} />
                        </InputGroup.Text>
                        <Form.Control
                          type="number"
                          onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                          name="countedQuantity"
                          value={movementForm.countedQuantity}
                          onChange={handleMovementFormChange}
                          placeholder="Quantité réellement comptée..."
                          isInvalid={!!formErrors.countedQuantity}
                          min="0"
                        />
                        <InputGroup.Text>unité(s)</InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {formErrors.countedQuantity}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    {/* CORRECTION: Affichage de la différence calculée */}
                    <Form.Group className="mb-3">
                      <Form.Label>Différence calculée</Form.Label>
                      <div className="p-3 bg-light rounded border">
                        {movementForm.countedQuantity && selectedStockItem ? (
                          (() => {
                            const { difference, isIncrease, isDecrease } = calculateAdjustmentDifference();
                            return (
                              <div>
                                <div className={`fw-bold mb-2 ${
                                  isIncrease ? 'text-success' : 
                                  isDecrease ? 'text-danger' : 'text-muted'
                                }`}>
                                  {difference === 0 ? (
                                    <span>⚖️ Stock inchangé (aucune différence)</span>
                                  ) : (
                                    <span>
                                      {isIncrease ? '📈 Augmentation de +' : '📉 Diminution de -'}{Math.abs(difference)} unité(s)
                                    </span>
                                  )}
                                </div>
                                <small className="text-muted d-block">
                                  <strong>Résultat :</strong> Le stock passera de {selectedStockItem.quantity} à {movementForm.countedQuantity} unité(s)
                                </small>
                                <small className="text-muted d-block">
                                  <strong>Historique :</strong> Un mouvement de {difference > 0 ? 'correction positive' : difference < 0 ? 'correction négative' : 'vérification'} sera enregistré
                                </small>
                              </div>
                            );
                          })()
                        ) : (
                          <span className="text-muted">Saisissez la quantité comptée pour voir l'impact</span>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            ) : (
              // Section classique pour entrées et sorties
              <Row>
                <Col md={movementForm.type === 'ENTREE' ? 6 : 12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Quantité *</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="number"
                        onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                        name="quantity"
                        value={movementForm.quantity}
                        onChange={handleMovementFormChange}
                        placeholder="Quantité du mouvement..."
                        isInvalid={!!formErrors.quantity}
                        min="1"
                      />
                      <InputGroup.Text>unité(s)</InputGroup.Text>
                    </InputGroup>
                    <Form.Control.Feedback type="invalid">
                      {formErrors.quantity}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                
                {/* Coût unitaire seulement pour les entrées */}
                {movementForm.type === 'ENTREE' && (
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Coût unitaire</Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="number"
                          onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                          name="unitCost"
                          value={movementForm.unitCost}
                          onChange={handleMovementFormChange}
                          placeholder="0.00"
                          isInvalid={!!formErrors.unitCost}
                          min="0"
                          step="0.01"
                        />
                        <InputGroup.Text>FCFA</InputGroup.Text>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid">
                        {formErrors.unitCost}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                )}
              </Row>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Raison {movementForm.type === 'AJUSTEMENT' && '*'}</Form.Label>
              <Form.Control
                type="text"
                name="reason"
                value={movementForm.reason}
                onChange={handleMovementFormChange}
                placeholder={
                  movementForm.type === 'AJUSTEMENT' ? 
                  "Ex: Inventaire mensuel, Produits endommagés..." :
                  "Raison du mouvement..."
                }
                isInvalid={!!formErrors.reason}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.reason}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notes"
                value={movementForm.notes}
                onChange={handleMovementFormChange}
                placeholder="Notes additionnelles..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowMovementModal(false)}>
              Annuler
            </Button>
            <Button 
              type="submit" 
              variant="primary" 
              disabled={submitting}
            >
              {submitting ? <Spinner animation="border" size="sm" /> : 'Créer le mouvement'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal Historique des mouvements */}
      <Modal 
        show={showMovementHistoryModal} 
        onHide={() => setShowMovementHistoryModal(false)} 
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="d-flex align-items-center gap-2">
              <Eye size={20} />
              Historique des mouvements
              {selectedStockItem && (
                <Badge bg="primary" className="ms-2">
                  {selectedStockItem.productName}
                </Badge>
              )}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingMovements ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Chargement des mouvements...</p>
            </div>
          ) : selectedProductMovements.length === 0 ? (
            <div className="text-center py-5">
              <Package size={48} className="text-muted mb-3" />
              <h6 className="text-muted">Aucun mouvement trouvé</h6>
              <p className="text-muted mb-0">
                Aucun historique de mouvement pour ce produit
              </p>
            </div>
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  {selectedProductMovements.length} mouvement(s) trouvé(s)
                </h6>
                <small className="text-muted">
                  Du plus récent au plus ancien
                </small>
              </div>
              <div className="movements-list">
                {selectedProductMovements.map((movement, index) => (
                  <Card key={movement.id || index} className="mb-2 border-start border-3" 
                        style={{
                          borderLeftColor: movement.type === 'ENTREE' ? '#28a745' : 
                                         movement.type === 'SORTIE' ? '#dc3545' : 
                                         '#17a2b8'
                        }}>
                    <Card.Body className="py-3">
                      <Row className="align-items-center">
                        <Col xs={2}>
                          <div className={`movement-icon bg-${getMovementColor(movement.type)}`}>
                            {getMovementIcon(movement.type)}
                          </div>
                        </Col>
                        <Col xs={10}>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <div>
                              <div className="fw-semibold">
                                {getMovementLabel(movement.type)}
                              </div>
                              <small className="text-muted">
                                {new Date(movement.date || movement.createdAt).toLocaleString('fr-FR', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: '2-digit',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </small>
                            </div>
                            <div className="text-end">
                              <div className={`fw-bold ${getMovementQuantityColor(movement)}`}>
                                {formatMovementQuantity(movement)} unité(s)
                              </div>
                              {movement.unitCost > 0 && (
                                <small className="text-muted">
                                  {movement.unitCost.toLocaleString('fr-FR')} FCFA/u
                                </small>
                              )}
                            </div>
                          </div>
                          
                          {movement.reason && (
                            <div className="mb-1">
                              <small className="text-muted">
                                <strong>Raison :</strong> {movement.reason}
                              </small>
                            </div>
                          )}
                          
                          {movement.reference && (
                            <div className="mb-1">
                              <small className="text-muted">
                                <strong>Référence :</strong> {movement.reference}
                              </small>
                            </div>
                          )}
                          
                          {movement.user && (
                            <div>
                              <small className="text-muted">
                                <strong>Par :</strong> {movement.user.name || movement.user}
                              </small>
                            </div>
                          )}
                          
                          <div className="mt-2 pt-2 border-top">
                            <small className="text-muted d-flex justify-content-between">
                              <span>Impact sur le stock :</span>
                              <span className={`fw-semibold ${getMovementQuantityColor(movement)}`}>
                                {formatMovementQuantity(movement)}
                              </span>
                            </small>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMovementHistoryModal(false)}>
            Fermer
          </Button>
          {selectedStockItem && (
            <Button 
              variant="primary" 
              onClick={() => {
                setShowMovementHistoryModal(false);
                handleCreateMovement(selectedStockItem);
              }}
            >
              <Plus size={14} className="me-1" />
              Nouveau mouvement
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      {/* Modal Modifier seuil */}
      <Modal show={showThresholdModal} onHide={() => setShowThresholdModal(false)}>
  <Form onSubmit={handleSubmitThreshold}>
    <Modal.Header closeButton>
      <Modal.Title>
        <Edit size={20} className="me-2" />
        Modifier les seuils d'alerte
      </Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {formErrors.submit && (
        <Alert variant="danger">{formErrors.submit}</Alert>
      )}
      
      {/* ✅ AJOUT : Affichage du stock actuel pour contexte */}
      {selectedStockItem && (
        <Alert variant="info" className="mb-3">
          <div className="d-flex justify-content-between align-items-center">
            <strong>{selectedStockItem.productName}</strong>
            <Badge bg="primary">Stock actuel: {selectedStockItem.quantity}</Badge>
          </div>
        </Alert>
      )}
     
      <Form.Group className="mb-3">
        <Form.Label>Seuil minimum d'alerte</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <AlertTriangle size={16} />
          </InputGroup.Text>
          <Form.Control
            type="number"
            onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
            name="threshold"
            value={thresholdForm.threshold}
            onChange={(e) => setThresholdForm({...thresholdForm, threshold: e.target.value})}
            placeholder="Seuil minimum..."
            isInvalid={!!formErrors.threshold}
            min="0"
          />
          <InputGroup.Text>unité(s)</InputGroup.Text>
        </InputGroup>
        <Form.Control.Feedback type="invalid">
          {formErrors.threshold}
        </Form.Control.Feedback>
        <Form.Text className="text-muted">
          Une alerte sera générée quand le stock sera inférieur ou égal à ce seuil.
        </Form.Text>
      </Form.Group>

      {/* ✅ AJOUT : Champ pour seuil maximum */}
      <Form.Group className="mb-3">
        <Form.Label>Seuil maximum (optionnel)</Form.Label>
        <InputGroup>
          <InputGroup.Text>
            <TrendingUp size={16} />
          </InputGroup.Text>
          <Form.Control
            type="number"
            onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
            name="maxThreshold"
            value={thresholdForm.maxThreshold || ''}
            onChange={(e) => setThresholdForm({...thresholdForm, maxThreshold: e.target.value})}
            placeholder="Seuil maximum..."
            isInvalid={!!formErrors.maxThreshold}
            min="1"
          />
          <InputGroup.Text>unité(s)</InputGroup.Text>
        </InputGroup>
        <Form.Control.Feedback type="invalid">
          {formErrors.maxThreshold}
        </Form.Control.Feedback>
        <Form.Text className="text-muted">
          Seuil pour détecter un surstock (doit être supérieur au minimum).
        </Form.Text>
      </Form.Group>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={() => setShowThresholdModal(false)}>
        Annuler
      </Button>
      <Button
        type="submit"
        variant="primary"
        disabled={submitting}
      >
        {submitting ? <Spinner animation="border" size="sm" /> : 'Mettre à jour les seuils'}
      </Button>
    </Modal.Footer>
  </Form>
</Modal>

      {/* Styles CSS */}
      <style jsx>{`
        .stock-container {
          background: #f8fafc;
          min-height: 100vh;
          padding: 1.5rem;
        }

        .stock-content {
          max-width: 1400px;
          margin: 0 auto;
        }

        .stock-header {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 10px rgba(0,0,0,0.08);
        }

        .header-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .header-icon {
          color: #3b82f6;
          flex-shrink: 0;
        }

        .stock-title {
          font-size: 2rem;
          font-weight: 700;
          color: #1a202c;
          margin: 0;
        }

        .stock-subtitle {
          color: #64748b;
          margin: 0;
          font-size: 0.95rem;
        }

        .stat-card {
          transition: all 0.2s ease;
          border: 1px solid #e2e8f0;
          margin: 10px;

        }

        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        .stat-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1rem;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          margin: 0;
          color: #1a202c;
        }

        .stat-label {
          color: #64748b;
          margin: 0;
          font-size: 0.9rem;
        }

        .stock-card {
          transition: all 0.2s ease;
          border: 1px solid #e2e8f0;
          height: 100%;
        }

        .stock-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        .product-image-container {
          width: 50px;
          height: 50px;
          border-radius: 8px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          flex-shrink: 0;
        }

        .product-image-table {
          width: 40px;
          height: 40px;
          border-radius: 6px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
          flex-shrink: 0;
        }

        .movements-list {
          max-height: 400px;
          overflow-y: auto;
        }

        .movement-icon {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          color: white;
        }

        .movements-list::-webkit-scrollbar {
          width: 6px;
        }

        .movements-list::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .movements-list::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .table-responsive::-webkit-scrollbar {
          height: 8px;
        }

        .table-responsive::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .table-responsive::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .table-responsive::-webkit-scrollbar-thumb:hover {
          background: #a1a1a1;
        }

        /* Styles pour les signes des mouvements */
        .text-success {
          color: #28a745 !important;
        }

        .text-danger {
          color: #dc3545 !important;  
        }

        .text-info {
          color: #17a2b8 !important;
        }

        /* Animation pour les quantités */
        .movement-quantity {
          transition: all 0.2s ease;
        }

        .movement-quantity:hover {
          transform: scale(1.05);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .product-image-container {
            width: 40px;
            height: 40px;
          }
          
          .product-image-table {
            width: 35px;
            height: 35px;
          }
          
          .movement-icon {
            width: 28px;
            height: 28px;
            font-size: 12px;
          }

          .stock-header {
            padding: 1.5rem;
          }

          .stock-title {
            font-size: 1.5rem;
          }
        }

        @media (max-width: 576px) {
          .product-image-container {
            width: 35px;
            height: 35px;
          }
          
          .stat-number {
            font-size: 1.5rem;
          }

          .stock-container {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default StockManagement;